import { useCallback, useState } from "react";
import { useUniversalLibrary } from "@/contexts/UniversalLibraryContext";
import { useNavigate } from "react-router-dom";
import { LibraryItem } from "@/contexts/UniversalLibraryContext/types";
import { useToast } from "@/hooks/errors/use-toast";

/**
 * Handles universal-library item click according to the content type's config,
 * with robust error handling and logging for reliability/diagnostics.
 */
export function useUniversalLibraryItemClick() {
  const { contentTypes, selectedContentType } = useUniversalLibrary();
  const [lightboxState, setLightboxState] = useState<{ item: LibraryItem | null, type: string | null }>({
    item: null,
    type: null,
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle item click with defensive checks and robust logs
  const handleItemClick = useCallback(
    (item: LibraryItem) => {
      console.log('[UniversalLibraryItemClick] Item clicked:', {
        id: item?.id,
        contentType: item?.contentType,
        title: item?.title,
        hasImageUrl: !!item?.imageUrl,
        item: item,
        timestamp: new Date().toISOString()
      });

      // Defensive: ensure item and type exist
      const type = item?.contentType;
      if (!item || !type) {
        console.error('[UniversalLibraryItemClick] Missing item or type:', { item, type });
        toast({
          variant: "destructive",
          title: "Could not open item",
          description: "Item or content type missing. Please refresh and try again.",
        });
        console.error(
          "[UniversalLibrary] Click failed — missing item or type",
          { item }
        );
        return;
      }

      const config = contentTypes[type];
      console.log('[UniversalLibraryItemClick] Content type config:', {
        type,
        hasConfig: !!config,
        config: config,
        availableTypes: Object.keys(contentTypes),
        timestamp: new Date().toISOString()
      });

      if (!config) {
        console.error('[UniversalLibraryItemClick] No config found for type:', { type, contentTypes });
        toast({
          variant: "destructive",
          title: "Unsupported Content Type",
          description: `No configuration found for type "${type}". Please contact support.`,
        });
        console.error(
          "[UniversalLibrary] No ContentTypeConfig found for type:",
          { type, item, contentTypes }
        );
        return;
      }

      // Guard: check for actionType (should be string 'lightbox' or 'navigate')
      if (!config.actionType) {
        console.error('[UniversalLibraryItemClick] Missing actionType:', { config, type });
        toast({
          variant: "destructive",
          title: "Library Configuration Error",
          description: `Content type "${type}" was registered without an action type. Please report this issue.`,
        });
        console.error(
          "[UniversalLibrary] ContentTypeConfig for type missing actionType",
          { config, item }
        );
        return;
      }

      console.log('[UniversalLibraryItemClick] Processing action:', {
        actionType: config.actionType,
        hasCustomHandler: typeof config.onItemClick === "function",
        timestamp: new Date().toISOString()
      });

      // Custom click absolute escape hatch
      if (typeof config.onItemClick === "function") {
        console.log('[UniversalLibraryItemClick] Executing custom click handler');
        try {
          config.onItemClick(item);
        } catch (error) {
          console.error('[UniversalLibraryItemClick] Custom handler error:', error);
          toast({
            variant: "destructive",
            title: "Custom Click Handler Failed",
            description: "The custom click handler threw an error. See console.",
          });
          console.error("[UniversalLibrary] onItemClick error", error, { item, config });
        }
        return;
      }

      // Main actionType switcher
      switch (config.actionType) {
        case "lightbox":
          console.log('[UniversalLibraryItemClick] Opening lightbox for:', { type, itemId: item.id });
          setLightboxState({ item, type });
          console.log('[UniversalLibraryItemClick] Lightbox state set:', { item: item.id, type });
          break;
        case "navigate":
          console.log('[UniversalLibraryItemClick] Navigating for:', { type, itemId: item.id });
          if (typeof config.getRoute === "function") {
            try {
              const route = config.getRoute(item);
              console.log('[UniversalLibraryItemClick] Navigation route:', { route, type, itemId: item.id });
              if (route) {
                navigate(route);
              } else {
                console.error('[UniversalLibraryItemClick] Empty route returned:', { item, config });
                toast({
                  variant: "destructive",
                  title: "No Navigation Route",
                  description: "Could not determine app page for this item.",
                });
                console.error(
                  "[UniversalLibrary] getRoute returned empty/falsy",
                  { item, config }
                );
              }
            } catch (error) {
              console.error('[UniversalLibraryItemClick] Navigation error:', error);
              toast({
                variant: "destructive",
                title: "Navigation Error",
                description: "Failed to get navigation route for this item. See console.",
              });
              console.error("[UniversalLibrary] getRoute threw error", error, { item, config });
            }
          } else {
            console.error('[UniversalLibraryItemClick] No getRoute handler:', { config, type });
            toast({
              variant: "destructive",
              title: "Navigation Handler Missing",
              description: "No getRoute handler provided for this content type.",
            });
            console.error("[UniversalLibrary] No getRoute handler", { config, item });
          }
          break;
        default: // UNRECOGNIZED actionType or unsupported type
          console.error('[UniversalLibraryItemClick] Unknown actionType:', { actionType: config.actionType, config, type });
          toast({
            variant: "destructive",
            title: "Unknown ActionType",
            description: `No valid click action found for "${type}". Please refresh or report a bug.`,
          });
          console.error("[UniversalLibrary] Unhandled actionType", { config, item });
      }
    },
    [contentTypes, navigate, toast]
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxState({ item: null, type: null });
  }, []);

  // Handle navigation between items in lightbox
  const handleNavigateToItem = useCallback((item: LibraryItem) => {
    setLightboxState({ item, type: item.contentType });
  }, []);

  return {
    handleItemClick,
    lightboxItem: lightboxState.item,
    lightboxType: lightboxState.type,
    handleCloseLightbox,
    handleNavigateToItem,
    setLightboxState,
  };
}
